import { inject } from '@angular/core';
import { CanMatchFn, Router } from '@angular/router';
import { of } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import * as Sentry from '@sentry/angular';

import { isLoginLegalDocsPath, LOGIN_LEGAL_DOCS_PATH } from '@brightside-web/shared/desktop';

import { DocumentsKeyEnum, LegalDocumentsService } from './legal-documents.service';

/** Upper bound so a stalled request cannot deadlock route matching. */
const LEGAL_DOCS_CHECK_TIMEOUT_MS = 8000;

/**
 * Sends a signed-in client to `/home/legal` while the login legal packet still has documents
 * to accept.
 *
 * This is the `canMatch` form of the old `homeLegalGuard`, which was a `canActivate` on the
 * feed route. It has to run as a `canMatch` and ahead of `onboardingMatchCompleteGuard`,
 * because that guard redirects to `/onboarding` at match time — so a client part-way through
 * onboarding never reached the feed and was never shown pending terms. Login terms come
 * immediately after sign-in, before eligibility and onboarding.
 *
 * No documents means no redirect, matching the existing `HasLegalDocsGuard`. Errors fail open;
 * see `docs/NOTES.md` for why.
 */
export const loginLegalDocsMatchGuard: CanMatchFn = (route, segments) => {
  if (isLoginLegalDocsPath(segments)) {
    return of(true);
  }

  const legalDocumentsService = inject(LegalDocumentsService);
  const router = inject(Router);

  return legalDocumentsService.getDocsByKey(DocumentsKeyEnum.LOGIN).pipe(
    map((response) => {
      const documents = response.payload?.legalDocuments;

      if (documents && documents.length > 0) {
        return router.parseUrl(`/${LOGIN_LEGAL_DOCS_PATH.join('/')}`);
      }

      return true;
    }),
    // Reported at warning level because Angular subscribes every canMatch guard concurrently,
    // so this fires once per client during an outage even when an earlier guard wins.
    timeout(LEGAL_DOCS_CHECK_TIMEOUT_MS),
    catchError((error) => {
      Sentry.captureException(error, {
        level: 'warning',
        tags: { guard: 'login-legal-docs' },
      });
      return of(true);
    }),
  );
};
